import {
  DSDropdown,
  DSInput,
  DSTextarea,
} from "@components/design-system/molecules";
import {
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";

import AgeInput from "./AgeSelection";
import CreatePatientBreedDropdown from "./BreedDropdown";
import { IntactCheckbox, SexSelectionButton } from "./SexSelection";
import { Breeds } from "../../../assets/data/Breeds";
import HeadingWrapper from "../../common/HeadingWrapper";
import { RequiredAsterisk } from "../../common/RequiredAsterisk";

export default function CreatePatientForm({
  control,
  errors,
  sex,
  setSex,
  intact,
  setIntact,
  selectedSpecies,
  setSelectedSpecies,
  isAgeInputTypeDOB,
  setIsAgeInputTypeDOB,
  speciesItems,
}) {
  const [breedsList, setBreedsList] = useState([
    "No breed for this species. Try typing in a custom one!",
  ]);
  const [shouldShowMore, setShouldShowMore] = useState(false);

  useEffect(() => {
    function getBreeds() {
      if (selectedSpecies) {
        if (Breeds[selectedSpecies.name]) {
          setBreedsList(Breeds[selectedSpecies.name]);
        } else {
          setBreedsList([
            "No preset breeds for this species. Try typing in a custom one!",
          ]);
        }
      }
    }
    getBreeds();
  }, [selectedSpecies]);

  function handleSelectedItemChange({ selectedItem }) {
    setSelectedSpecies(selectedItem);
  }

  return (
    <form>
      <div className="grid md:grid-cols-2 md:gap-6">
        <div className="space-y-2 md:space-y-6">
          <div>
            <HeadingWrapper
              heading={
                <h4>
                  Name <RequiredAsterisk />
                </h4>
              }
            />
            <div className="space-y-2">
              <Controller
                control={control}
                name="patientName"
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <DSInput
                    placeholder="Patient's name"
                    dataTestId="pFirstName"
                    error={
                      errors.patientName ? "Required" : undefined
                    }
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="ownersLastName"
                rules={{
                  required: false,
                }}
                render={({ field }) => (
                  <DSInput
                    placeholder="Owner's last name"
                    dataTestId="pLastName"
                    {...field}
                  />
                )}
              />
            </div>
          </div>

          <div>
            <HeadingWrapper heading={<h4>Species</h4>} />
            <Controller
              control={control}
              name="selectedSpecies"
              render={({ field }) => (
                <DSDropdown
                  options={speciesItems}
                  value={selectedSpecies?.name ?? null}
                  onChange={(_value, option) => {
                    handleSelectedItemChange({
                      selectedItem: option,
                    });
                    field.onChange(option);
                  }}
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.name}
                  placeholder="Select a species..."
                  dataTestId="pSpeciesDropdown"
                  width="w-full"
                />
              )}
            />
          </div>
          <div>
            <HeadingWrapper heading={<h4>Breed</h4>} />
            <Controller
              control={control}
              name="breed"
              render={({ field }) => (
                <CreatePatientBreedDropdown
                  {...field}
                  items={breedsList}
                />
              )}
            />
          </div>
        </div>
        <div className="space-y-6 justify-self-center">
          <div>
            <AgeInput
              isAgeInputTypeDOB={isAgeInputTypeDOB}
              control={control}
            />
            <div className="space-x-5 mt-2 px-3">
              <label className="text-sm text-secondary">
                <input
                  className="text-indigo-500 focus:ring-0"
                  type="radio"
                  name="AgeInputBirthday"
                  checked={true === isAgeInputTypeDOB}
                  onChange={() => {
                    setIsAgeInputTypeDOB(true);
                  }}
                  value="Enter DOB"
                />{" "}
                Enter D.O.B.
              </label>
              <label className="text-sm text-secondary">
                <input
                  className="text-indigo-500 focus:ring-0"
                  type="radio"
                  name="AgeInputAge"
                  checked={false === isAgeInputTypeDOB}
                  onChange={() => {
                    setIsAgeInputTypeDOB(false);
                  }}
                  value="Enter Age"
                />{" "}
                Enter Age
              </label>
            </div>
          </div>
          <div>
            <HeadingWrapper
              heading={
                <h4>
                  Sex <RequiredAsterisk />
                </h4>
              }
            />
            <div className="flex flex-row items-center justify-center flex-wrap mt-2">
              <SexSelectionButton
                type="female"
                setSex={setSex}
                sex={sex}
              />
              <SexSelectionButton
                type="male"
                setSex={setSex}
                sex={sex}
              />
            </div>

            <div className="flex items-center justify-center">
              {sex !== null ? (
                <IntactCheckbox
                  sex={sex}
                  intact={intact}
                  setIntact={setIntact}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className="pt-2">
        <span
          className="cursor-pointer text-sm text-primary hover:text-brand"
          onClick={() => setShouldShowMore(!shouldShowMore)}
        >
          More (optional){" "}
          <FontAwesomeIcon
            icon={shouldShowMore ? faChevronUp : faChevronDown}
            size="xs"
          />
        </span>
        {shouldShowMore ? (
          <div className="pt-2">
            <div className="grid md:grid-cols-2 md:gap-6">
              <div>
                <PatientMoreInputs
                  control={control}
                  fieldName={"color"}
                  label={"Color"}
                  placeholder={
                    "ex: coat/skin, distinctive markings, etc."
                  }
                />
                <PatientTextArea
                  control={control}
                  fieldName={"ownerContactInfo"}
                  label={"Owner's contact info"}
                  placeholder={
                    "ex: name, address and telephone number(s)"
                  }
                />
              </div>
              <div>
                <PatientMoreInputs
                  control={control}
                  fieldName={"microchip"}
                  label={"Microchip #"}
                  placeholder={"ex: 981xxxxXXXXXXXX"}
                />
                <PatientTextArea
                  control={control}
                  fieldName={"emergencyContactInfo"}
                  label={"Emergency contact info"}
                  placeholder={
                    "ex: name, address and telephone number(s)"
                  }
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </form>
  );
}

function PatientTextArea({ control, fieldName, label, placeholder }) {
  return (
    <Controller
      control={control}
      name={fieldName}
      rules={{
        required: false,
      }}
      render={({ field }) => (
        <div className="py-2">
          <HeadingWrapper heading={<h4>{label}</h4>} />
          <DSTextarea
            minRows={3}
            dataTestId={fieldName}
            {...field}
            placeholder={placeholder}
          />
        </div>
      )}
    />
  );
}

function PatientMoreInputs({
  control,
  fieldName,
  label,
  placeholder,
}) {
  return (
    <Controller
      control={control}
      name={fieldName}
      rules={{
        required: false,
      }}
      render={({ field }) => (
        <div className="py-2">
          <HeadingWrapper heading={<h4>{label}</h4>} />
          <DSInput
            placeholder={placeholder}
            dataTestId={fieldName}
            {...field}
          />
        </div>
      )}
    />
  );
}
